import {
  ComputedRef,
  computed,
  defineComponent,
  DeepReadonly,
  getCurrentInstance,
  h,
  inject,
  onMounted,
  onUnmounted,
  onUpdated,
  provide,
  ref,
  SetupContext,
  useId,
} from 'vue'

import { DEFAULT_CLASS_PREFIX, injectCarousel } from '@/shared'
import { disableChildrenTabbing, restoreChildrenTabbing } from '@/utils'

import { SlideProps } from './Slide.types'

export const Slide = defineComponent({
  name: 'CarouselSlide',
  props: {
    id: {
      type: String,
      default: (props: { isClone?: boolean }) => (props.isClone ? undefined : useId()),
    },
    index: {
      type: Number,
      default: undefined,
    },
    isClone: {
      type: Boolean,
      default: false,
    },
  },
  setup(props: DeepReadonly<SlideProps>, { attrs, slots, expose }: SetupContext) {
    const carousel = inject(injectCarousel)
    provide(injectCarousel, undefined) // Don't provide for nested slides

    if (!carousel) {
      return () => '' // Don't render, let vue warn about the missing provide
    }

    const currentIndex = ref(props.index)

    const setIndex = (newIndex: number) => {
      currentIndex.value = newIndex
    }

    const instance = getCurrentInstance()!

    const getBoundingRect = () => {
      const el = instance.vnode.el as HTMLElement
      return el ? el.getBoundingClientRect() : { width: 0, height: 0 }
    }

    expose({
      id: props.id,
      setIndex,
      getBoundingRect,
    })

    // A marquee has no current slide, and every slide passes through the viewport
    const isActive: ComputedRef<boolean> = computed(
      () => !carousel.isMarquee && currentIndex.value === carousel.activeSlide
    )
    const isPrev: ComputedRef<boolean> = computed(
      () => !carousel.isMarquee && currentIndex.value === carousel.activeSlide - 1
    )
    const isNext: ComputedRef<boolean> = computed(
      () => !carousel.isMarquee && currentIndex.value === carousel.activeSlide + 1
    )
    const isVisible: ComputedRef<boolean> = computed(
      () =>
        carousel.isMarquee ||
        (currentIndex.value >= carousel.visibleRange.min &&
          currentIndex.value <= carousel.visibleRange.max)
    )

    const slideStyle = computed(() => {
      if (carousel.config.itemsToShow === 'auto') {
        return
      }
      const itemsToShow = carousel.config.itemsToShow
      const dimension =
        carousel.config.gap > 0 && itemsToShow > 1
          ? `calc(${100 / itemsToShow}% - ${
              (carousel.config.gap * (itemsToShow - 1)) / itemsToShow
            }px)`
          : `${100 / itemsToShow}%`

      return carousel.isVertical ? { height: dimension } : { width: dimension }
    })

    // Focus caused by a pointer (mouse click or the compat mousedown after a
    // touch tap) must not navigate; only keyboard/programmatic focus should.
    let isPointerFocus = false
    let pointerFocusTimer: ReturnType<typeof setTimeout> | null = null

    const handleMousedown = () => {
      isPointerFocus = true
      if (pointerFocusTimer) {
        clearTimeout(pointerFocusTimer)
      }
      // The browser focuses synchronously after mousedown; a macrotask runs after that.
      pointerFocusTimer = setTimeout(() => {
        isPointerFocus = false
        pointerFocusTimer = null
      }, 0)
    }

    carousel.slideRegistry.registerSlide(instance, props.index)
    onUnmounted(() => {
      carousel.slideRegistry.unregisterSlide(instance)
      if (pointerFocusTimer) {
        clearTimeout(pointerFocusTimer)
      }
    })

    // Keep focusable content of clones and off-screen slides out of the tab order.
    // Slides re-render on every navigation, so only query the DOM when this
    // slide has actually disabled something.
    let hasDisabledChildren = false
    const updateChildrenTabbing = () => {
      if (!carousel.config.enabled) {
        return
      }
      if (props.isClone || !isVisible.value) {
        disableChildrenTabbing(instance.vnode)
        hasDisabledChildren = true
      } else if (hasDisabledChildren) {
        restoreChildrenTabbing(instance.vnode)
        hasDisabledChildren = false
      }
    }
    onMounted(updateChildrenTabbing)
    onUpdated(updateChildrenTabbing)

    return () => {
      if (!carousel.config.enabled) {
        return slots.default?.()
      }

      const prefix = carousel.config.classPrefix || DEFAULT_CLASS_PREFIX

      return h(
        'li',
        {
          style: [attrs.style, { ...slideStyle.value }],
          class: {
            [`${prefix}__slide`]: true,
            [`${prefix}__slide--clone`]: props.isClone,
            [`${prefix}__slide--visible`]: isVisible.value,
            [`${prefix}__slide--active`]: isActive.value,
            [`${prefix}__slide--prev`]: isPrev.value,
            [`${prefix}__slide--next`]: isNext.value,
            [`${prefix}__slide--sliding`]: carousel.isSliding,
          },
          onMousedownCapture: handleMousedown,
          onFocusin: () => {
            // Prevent the viewport being scrolled by the focus (in native mode
            // that scroll is the navigation itself)
            if (carousel.viewport && !carousel.isNative) {
              carousel.viewport.scrollLeft = 0
              carousel.viewport.scrollTop = 0
            }
            if (isPointerFocus || carousel.config.autoScrollOnFocus === false) {
              return
            }
            carousel.nav.slideTo(currentIndex.value)
          },
          id: props.isClone ? undefined : props.id,
          'aria-hidden': props.isClone || undefined,
          tabindex: props.isClone || !isVisible.value ? -1 : undefined,
        },
        slots.default?.({
          currentIndex: currentIndex.value,
          isActive: isActive.value,
          isClone: props.isClone,
          isPrev: isPrev.value,
          isNext: isNext.value,
          isSliding: carousel.isSliding,
          isVisible: isVisible.value,
        })
      )
    }
  },
})
